import React from 'react';

import isEmpty from 'lodash/fp/isEmpty';
import map from 'lodash/fp/map';

import { reducer as articleReducer } from '@lumapps/articles/ducks/slice';
import { useDataAttributes } from '@lumapps/data-attributes';
import { HEADER } from '@lumapps/header/keys';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { Button } from '@lumapps/lumx/react';
import { injectReducer } from '@lumapps/redux/manager';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useSpaceContributionButton } from '../../hooks/useSpaceContributionButton';
import { SpaceContentContributionMenu } from '../SpaceContentContributionMenu';

injectReducer('articles', articleReducer);

const SCOPE = 'space-contribution';

export interface SpaceContributionButtonProps {
    isPreview?: boolean;
}

export const SpaceContributionButton: React.FC<SpaceContributionButtonProps> = ({ isPreview = false }) => {
    // Shared ref for the element that opens the dialogs (the single create button or the menu
    // trigger button). Passed to the dialogs so focus returns to it when they close.
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(SCOPE);

    const { menuItems, dialogs, shouldShowContentTypesSection } = useSpaceContributionButton(triggerRef);

    if (isPreview) {
        const previewButtonProps = { style: { pointerEvents: 'none' }, tabIndex: -1 };

        return (
            <Button {...get({ element: 'button', action: 'in-preview' })} {...previewButtonProps}>
                {translateKey(GLOBAL.CREATE)}
            </Button>
        );
    }

    if (isEmpty(menuItems) && !shouldShowContentTypesSection) {
        return dialogs;
    }

    if (menuItems.length === 1 && !shouldShowContentTypesSection) {
        const { key, onClick, tooltipLabelKey } = menuItems[0];

        return (
            <>
                <Button
                    {...get({ element: 'button', action: `create-${key}` })}
                    onClick={onClick}
                    ref={triggerRef}
                    title={translateKey(tooltipLabelKey)}
                >
                    {translateKey(GLOBAL.CREATE)}
                </Button>
                {dialogs}
            </>
        );
    }

    return (
        <>
            <Menu.Trigger popoverProps={{ placement: 'bottom-start', width: 'xxl' }}>
                <Menu.TriggerButton as={Button} ref={triggerRef} {...get({ element: 'button', action: 'open-menu' })}>
                    {translateKey(GLOBAL.CREATE)}
                </Menu.TriggerButton>

                <Menu>
                    {map(
                        ({ key, labelKey, tooltipLabelKey, icon, onClick }) => (
                            <Menu.Item
                                {...get({ element: 'menu-item', action: `create-${key}` })}
                                key={key}
                                title={translateKey(tooltipLabelKey)}
                                icon={icon}
                                onClick={onClick}
                            >
                                {translateKey(labelKey)}
                            </Menu.Item>
                        ),
                        menuItems,
                    )}

                    {/* NEW CONTENT section with custom content types */}
                    {shouldShowContentTypesSection && (
                        <Menu.Section label={translateKey(HEADER.NEW_CONTENT)} {...get({ element: 'new-content' })}>
                            <SpaceContentContributionMenu />
                        </Menu.Section>
                    )}
                </Menu>
            </Menu.Trigger>
            {dialogs}
        </>
    );
};
